'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CAPTION_STYLES, CaptionStyleName } from '@/lib/utils/caption-styles';
import { Check } from 'lucide-react';

interface CaptionStyleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedStyle: CaptionStyleName;
  onSelectStyle: (style: CaptionStyleName) => void;
}

export function CaptionStyleModal({
  open,
  onOpenChange,
  selectedStyle,
  onSelectStyle,
}: CaptionStyleModalProps) {
  const handleSelect = (style: CaptionStyleName) => {
    onSelectStyle(style);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Choose Caption Style</DialogTitle>
          <DialogDescription>
            Select the caption style that will be burned into your exported video.
            The style determines font size, background, and visual appearance.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-4">
          {(Object.entries(CAPTION_STYLES) as [CaptionStyleName, typeof CAPTION_STYLES[CaptionStyleName]][]).map(([key, style]) => {
            const isSelected = key === selectedStyle;
            const isNone = key === 'none';

            return (
              <button
                key={key}
                onClick={() => handleSelect(key)}
                className={`
                  relative flex items-start gap-4 p-4 rounded-lg border-2 transition-all text-left
                  ${isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }
                `}
              >
                {/* Icon/Sample */}
                {isNone ? (
                  <div className="flex-shrink-0 w-12 h-12 rounded border-2 border-dashed border-muted-foreground/30 flex items-center justify-center text-muted-foreground">
                    <span className="text-2xl">∅</span>
                  </div>
                ) : (
                  <div
                    className="flex-shrink-0 w-12 h-12 rounded flex items-center justify-center text-lg font-bold"
                    style={{
                      backgroundColor: style.background.color,
                      opacity: style.background.opacity,
                      color: style.font.color,
                      fontSize: '14px',
                      borderRadius: style.background.borderRadius ? `${style.background.borderRadius}px` : '0',
                    }}
                  >
                    Aa
                  </div>
                )}

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-sm">{style.name}</h4>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {style.description}
                  </p>

                  {/* Style specs */}
                  {!isNone && (
                    <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                      <span className="bg-muted px-2 py-0.5 rounded">
                        {style.font.size}px
                      </span>
                      {style.background.borderRadius > 0 && (
                        <span className="bg-muted px-2 py-0.5 rounded">
                          Rounded
                        </span>
                      )}
                      <span className="bg-muted px-2 py-0.5 rounded">
                        {Math.round(style.background.opacity * 100)}% opacity
                      </span>
                    </div>
                  )}
                </div>

                {/* Preview */}
                {!isNone && (
                  <div className="flex-shrink-0">
                    <div
                      className="px-3 py-1 rounded text-center"
                      style={{
                        backgroundColor: style.background.color,
                        opacity: style.background.opacity,
                        color: style.font.color,
                        fontSize: `${style.font.size / 2}px`,
                        fontWeight: 'bold',
                        borderRadius: style.background.borderRadius ? `${style.background.borderRadius / 2}px` : '0',
                        textShadow: style.font.stroke ? `0 0 2px ${style.font.stroke}` : 'none',
                      }}
                    >
                      Sample
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
